import type { InvoiceAiAnalysisRow } from "../api/auditorApi";

export const MANUAL_ACT_INDEX_BASE = 900_000;
export const MANUAL_META_PREFIX = "__manual__|";

export type RowAmounts = {
  unit: number;
  total: number;
  grille: number;
  decimals: number;
  suffix: string;
};

export function roundMoney(amount: number, currency: string): number {
  if (currency === "CDF") {
    return Math.round(amount);
  }
  return Math.round(amount * 100) / 100;
}

function normalizeSynthesisHaystack(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

/** Ligne synthèse / total PDF : dernière ligne, jamais comptabilisée. */
export function isSynthesisRow(row: InvoiceAiAnalysisRow | null | undefined): boolean {
  if (!row) return false;
  if (row.is_synthesis || row.type_obs === "synthese") return true;
  const hay = normalizeSynthesisHaystack(`${row.act_name ?? ""} ${row.patient ?? ""}`);
  if (!hay) return false;
  return (
    /\b(synthese|recapitulatif|recap\b|total\s+(general|facture|global|ttc)|globale?\s+hors\s+convention|resume\s+(des\s+)?factures)\b/.test(
      hay,
    ) ||
    hay.includes("synthese globale") ||
    hay.includes("synthese factures") ||
    hay.includes("factures hors convention")
  );
}

export function sortAnalysisTable<T extends InvoiceAiAnalysisRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const as = isSynthesisRow(a);
    const bs = isSynthesisRow(b);
    if (as !== bs) return as ? 1 : -1;
    return String(a.date ?? "").localeCompare(String(b.date ?? ""));
  });
}

export type AiMoneySummary = {
  billedTotal: number;
  linesTotal: number;
  retainedTotal: number;
  refractedTotal: number;
};

export type AiColumnTotals = {
  qty: number;
  billed: number;
  grille: number;
  validated: number;
};

/** Totaux colonnes pour le pied de tableau (hors lignes synthèse PDF). */
export function computeAiColumnTotals(params: {
  rows: InvoiceAiAnalysisRow[];
  reviews: Record<number, { review_status?: string; montant_retenu?: number }>;
  corrections: Record<number, { corrected_qty?: number; corrected_price?: number }>;
  currency: string;
  exchangeRateCdf: number;
}): AiColumnTotals {
  const { rows, reviews, corrections, currency, exchangeRateCdf } = params;
  let qty = 0;
  let billed = 0;
  let grille = 0;
  let validated = 0;

  for (const row of rows) {
    if (isSynthesisRow(row)) continue;
    const idx = row.act_orig_index ?? -1;
    const amounts = analysisRowAmounts(row, currency, exchangeRateCdf);
    const corr = corrections[idx];
    const rowQty = Math.max(1, corr?.corrected_qty ?? row.qty ?? 1);
    qty += rowQty;
    billed = roundMoney(billed + amounts.total, currency);
    if (amounts.grille > 0) {
      grille = roundMoney(grille + amounts.grille * rowQty, currency);
    }

    const review = reviews[idx];
    const status = review?.review_status ?? "pending";
    if (status === "approved") {
      const v =
        review?.montant_retenu != null && !Number.isNaN(review.montant_retenu)
          ? Number(review.montant_retenu)
          : amounts.total;
      validated = roundMoney(validated + v, currency);
    } else if (status === "rejected") {
      // 0
    } else if (corr?.corrected_price != null) {
      validated = roundMoney(
        validated + Number(corr.corrected_price) * rowQty,
        currency,
      );
    }
  }

  return { qty, billed, grille, validated };
}

export function computeAiMoneySummary(params: {
  rows: InvoiceAiAnalysisRow[];
  reviews: Record<number, { review_status?: string; montant_retenu?: number }>;
  currency: string;
  exchangeRateCdf: number;
  billedTotal?: number;
  retainedOverride?: number | null;
}): AiMoneySummary {
  const { rows, reviews, currency, exchangeRateCdf } = params;
  let linesTotal = 0;
  let retainedFromReviews = 0;
  for (const row of rows) {
    if (isSynthesisRow(row)) continue;
    const idx = row.act_orig_index ?? -1;
    const amounts = analysisRowAmounts(row, currency, exchangeRateCdf);
    linesTotal += amounts.total;
    const status = reviews[idx]?.review_status ?? "pending";
    if (status === "approved") {
      const v = reviews[idx]?.montant_retenu;
      retainedFromReviews += v != null && !Number.isNaN(v) ? Number(v) : amounts.total;
    }
  }
  linesTotal = roundMoney(linesTotal, currency);
  const retainedTotal =
    params.retainedOverride != null && params.retainedOverride > 0
      ? roundMoney(params.retainedOverride, currency)
      : roundMoney(retainedFromReviews, currency);
  const billedTotal =
    params.billedTotal != null && params.billedTotal > 0
      ? roundMoney(params.billedTotal, currency)
      : linesTotal;
  return {
    billedTotal,
    linesTotal,
    retainedTotal,
    refractedTotal: roundMoney(Math.max(0, billedTotal - retainedTotal), currency),
  };
}

export function inferExchangeRateCdf(unitAmount: number, grilleUsd: number): number {
  if (grilleUsd <= 0 || grilleUsd >= 500 || unitAmount < 500) return 0;
  const ratio = unitAmount / grilleUsd;
  if (ratio < 500 || ratio > 50000) return 0;
  const rounded = Math.round(ratio);
  const common = [2300, 2400, 2500, 2600, 2750, 2800];
  for (const c of common) {
    if (Math.abs(c - rounded) / c < 0.06) return c;
  }
  return rounded;
}

export function inferExchangeRateFromTable(rows: InvoiceAiAnalysisRow[]): number {
  const ratios: number[] = [];
  for (const row of rows) {
    const rate = inferExchangeRateCdf(row.prix_facture ?? 0, row.prix_grille ?? 0);
    if (rate > 0) ratios.push(rate);
  }
  if (ratios.length === 0) return 0;
  ratios.sort((a, b) => a - b);
  return ratios[Math.floor(ratios.length / 2)] ?? 0;
}

/** Reprend invoice_analysis_row_amounts (legacy PHP). */
export function analysisRowAmounts(
  row: InvoiceAiAnalysisRow,
  currency: string,
  exchangeRateCdf = 0,
): RowAmounts {
  const qty = Math.max(1, row.qty ?? 1);
  const suffix = currency === "CDF" ? " FC" : " USD";
  const decimals = currency === "CDF" ? 0 : 2;

  if (currency === "CDF") {
    const rate = exchangeRateCdf > 0 ? exchangeRateCdf : 0;
    let unit = row.prix_facture_cdf ?? 0;
    if (unit <= 0) {
      const puUsd = row.prix_facture ?? 0;
      unit = rate > 0 && puUsd > 0 ? roundMoney(puUsd * rate, currency) : puUsd;
    }
    let total = row.total_facture_cdf ?? 0;
    if (total <= 0) {
      total = roundMoney(unit * qty, currency);
    }
    let grille = row.prix_grille_cdf ?? 0;
    if (grille <= 0) {
      const gUsd = row.prix_grille ?? 0;
      grille = rate > 0 && gUsd > 0 ? roundMoney(gUsd * rate, currency) : gUsd;
    }
    return { unit, total, grille, suffix, decimals };
  }

  let unit = row.prix_facture ?? 0;
  let total = row.total_facture ?? 0;
  if (total <= 0 && unit > 0) {
    total = roundMoney(unit * qty, currency);
  }
  let grille = row.prix_grille ?? 0;

  let rate = exchangeRateCdf;
  if (rate <= 0) {
    rate = inferExchangeRateCdf(unit, grille);
  }

  if (rate > 0) {
    const cdfUnit = row.prix_facture_cdf ?? 0;
    if (cdfUnit > 0 && unit >= rate * 0.5) {
      unit = roundMoney(cdfUnit / rate, currency);
      const cdfTotal = row.total_facture_cdf ?? 0;
      total =
        cdfTotal > 0
          ? roundMoney(cdfTotal / rate, currency)
          : roundMoney(unit * qty, currency);
    } else if (grille > 0 && grille < 500 && unit >= 500) {
      const converted = roundMoney(unit / rate, currency);
      if (converted <= grille * 10) {
        unit = converted;
        total =
          total > 0
            ? roundMoney(total / rate, currency)
            : roundMoney(unit * qty, currency);
      }
    }
  }

  return {
    unit,
    total,
    grille,
    suffix,
    decimals,
  };
}

export function rowIsAnomalyForReview(
  typeObs: string,
  reviewStatus: string | undefined,
  withReviews: boolean,
): boolean {
  if (typeObs === "ok" || typeObs === "synthese") return false;
  if (!withReviews) return true;
  const status = reviewStatus ?? "pending";
  return status !== "approved" && status !== "rejected";
}

export function obsRowClassName(typeObs: string, isAnomaly: boolean): string {
  if (typeObs === "synthese") return "bg-slate-100/80";
  if (!isAnomaly) return "bg-card";
  switch (typeObs) {
    case "surcharge":
    case "sans_proforma":
      return "bg-red-50/50";
    case "sous_facturation":
      return "bg-amber-50/50";
    case "non_tarifé":
      return "bg-card-muted/40";
    default:
      return "bg-amber-50/50";
  }
}

export function formatManualMeta(patient: string, date: string): string {
  return `${MANUAL_META_PREFIX}${patient}|${date}`;
}

export function parseManualMeta(note?: string | null): { patient: string; date: string } | null {
  if (!note?.startsWith(MANUAL_META_PREFIX)) return null;
  const rest = note.slice(MANUAL_META_PREFIX.length);
  const sep = rest.indexOf("|");
  if (sep < 0) return { patient: rest, date: "" };
  return { patient: rest.slice(0, sep), date: rest.slice(sep + 1) };
}

export function isManualActIndex(actIndex: number, maxAnalysisIndex: number): boolean {
  return actIndex >= MANUAL_ACT_INDEX_BASE || actIndex > maxAnalysisIndex;
}

export function nextManualActIndex(existing: number[]): number {
  const floor = MANUAL_ACT_INDEX_BASE;
  const max = existing.reduce((m, n) => (n >= floor ? Math.max(m, n) : m), floor);
  return max + 1;
}
